/**
 * 批量任务 Mapper
 * 
 * 负责 DTO ↔ 域模型 的转换和校验
 * 提供入站校验、适配、归一化功能
 * 
 * @version 1.0.0
 * @author Frontend Team
 * @since 2025-11-12
 */

import type {
  BatchFile,
  BatchSession,
  WorkItem,
  BatchSessionPhase,
  BatchFileStatus,
  WorkItemStatus,
  BundleState,
  BatchFileType,
} from './batch'

import type {
  BatchFileDTO,
  BatchSessionDTO,
  WorkItemDTO,
  SessionListItemDTO
} from './batch.dto'

// ============= 类型守卫和校验 =============

/**
 * 校验并转换 BatchSessionPhase
 */
export function validatePhase(phase: string): BatchSessionPhase {
  const validPhases: BatchSessionPhase[] = ['draft', 'ready', 'running', 'completed', 'failed', 'partial', 'cancelled']
  if (validPhases.includes(phase as BatchSessionPhase)) {
    return phase as BatchSessionPhase
  }
  console.warn(`Invalid BatchSessionPhase: ${phase}, fallback to 'draft'`)
  return 'draft'
}

/**
 * 校验并转换 BatchFileStatus（只负责解析）
 */
export function validateFileStatus(status: string): BatchFileStatus {
  const validStatuses: BatchFileStatus[] = ['uploaded', 'parsing', 'ready', 'failed']
  if (validStatuses.includes(status as BatchFileStatus)) {
    return status as BatchFileStatus
  }
  console.warn(`Invalid BatchFileStatus: ${status}, fallback to 'uploaded'`)
  return 'uploaded'
}

/**
 * 校验并转换 WorkItemStatus
 */
export function validateWorkItemStatus(status: string): WorkItemStatus {
  const validStatuses: WorkItemStatus[] = ['pending', 'running', 'succeeded', 'failed', 'skipped', 'cancelled']
  if (validStatuses.includes(status as WorkItemStatus)) {
    return status as WorkItemStatus
  }
  console.warn(`Invalid WorkItemStatus: ${status}, fallback to 'pending'`)
  return 'pending'
}

/**
 * 校验并转换 BundleState
 */
export function validateBundleState(state: string): BundleState {
  const validStates: BundleState[] = ['none', 'building', 'ready', 'expired', 'failed']
  if (validStates.includes(state as BundleState)) {
    return state as BundleState
  }
  return 'none'
}

/**
 * 校验并转换 BatchFileType
 */
export function validateFileType(type: string): BatchFileType {
  const validTypes: BatchFileType[] = ['text', 'markdown', 'srt', 'ass', 'ssa']
  if (validTypes.includes(type as BatchFileType)) {
    return type as BatchFileType
  }
  return 'text'
}

// ============= DTO → 域模型 Mapper =============

/**
 * BatchFileDTO → BatchFile
 */
export function mapBatchFileFromDTO(dto: BatchFileDTO): BatchFile {
  return {
    id: dto.id,
    fileUid: dto.fileUid,
    userId: dto.userId,
    originalName: dto.originalName,
    fileSize: dto.fileSize,
    fileType: validateFileType(dto.fileType),
    contentHash: dto.contentHash,
    rawContent: dto.rawContent,
    parsedContent: dto.parsedContent,
    totalChars: dto.totalChars,
    progress: dto.progress,
    status: validateFileStatus(dto.status),
    errorMessage: dto.errorMessage,
    detectedLanguage: dto.detectedLanguage,
    // V1.0解析响应字段
    estimateSeconds: dto.estimateSeconds,
    estimateCredits: dto.estimateCredits,
    encoding: dto.encoding,
    baseCharsPerSec: dto.baseCharsPerSec,
    warnings: dto.warnings,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt
  }
}

/**
 * BatchSessionDTO → BatchSession
 */
export function mapBatchSessionFromDTO(dto: BatchSessionDTO): BatchSession {
  return {
    id: dto.id,
    sessionUid: dto.sessionUid,
    userId: dto.userId,
    title: dto.title,
    description: dto.description,
    voice: dto.voice,
    audioFormat: dto.audioFormat,
    rate: dto.rate,
    pitch: dto.pitch,
    style: dto.style,
    totalFiles: dto.totalFiles,
    totalSegments: dto.totalSegments,
    totalChars: dto.totalChars,
    completedFiles: dto.completedFiles,
    failedFiles: dto.failedFiles,
    progress: dto.progress,
    phase: validatePhase(dto.phase),
    errorMessage: dto.errorMessage,
    bundleUrl: dto.bundleUrl,
    bundleState: validateBundleState(dto.bundleState),
    bundleReadyAt: dto.bundleReadyAt,
    bundleExpiresAt: dto.bundleExpiresAt,
    bundleEtag: dto.bundleEtag,
    bundleCountTotal: dto.bundleCountTotal,
    bundleCountSucceeded: dto.bundleCountSucceeded,
    bundleSizeBytes: dto.bundleSizeBytes,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    submittedAt: dto.submittedAt,
    finishedAt: dto.finishedAt,
    files: dto.files?.map(mapBatchFileFromDTO),
    workItems: dto.workItems?.map(mapWorkItemFromDTO)
  }
}

/**
 * WorkItemDTO → WorkItem
 */
export function mapWorkItemFromDTO(dto: WorkItemDTO): WorkItem {
  return {
    id: dto.id,
    workItemUid: dto.workItemUid,
    sessionUid: dto.sessionUid,
    fileUid: dto.fileUid,
    userId: dto.userId,
    sequenceIndex: dto.sequenceIndex,
    status: validateWorkItemStatus(dto.status),
    errorMessage: dto.errorMessage,
    attemptCount: dto.attemptCount,
    maxAttempts: dto.maxAttempts,
    priority: dto.priority,
    providerJobId: dto.providerJobId,  // 新增：第三方任务ID
    engine: dto.engine,  // 新增：使用的引擎
    audioUrl: dto.audioUrl,
    downloadUrl: dto.downloadUrl,  // 新增：预签名下载链接
    providerResultUrl: dto.providerResultUrl,  // 新增：第三方下载链接（保底方案）
    audioFormat: dto.audioFormat,
    audioSize: dto.audioSize,
    durationMs: dto.durationMs,
    subtitleUrl: dto.subtitleUrl,
    subtitleFormat: dto.subtitleFormat,
    summaryUrl: dto.summaryUrl,
    summaryFormat: dto.summaryFormat,
    wordBoundaryUrl: dto.wordBoundaryUrl,
    wordBoundaryFormat: dto.wordBoundaryFormat,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    startedAt: dto.startedAt,
    completedAt: dto.completedAt,
    processingTime: dto.processingTime
  }
}

/**
 * SessionListItemDTO → BatchSession (简化版)
 */
export function mapSessionListItemFromDTO(dto: SessionListItemDTO): BatchSession {
  return {
    id: dto.id,
    sessionUid: dto.sessionUid,
    userId: dto.userId,
    title: dto.title,
    description: dto.description,
    voice: dto.voice,
    audioFormat: dto.audioFormat,
    rate: dto.rate,
    pitch: dto.pitch,
    style: dto.style,
    totalFiles: dto.totalFiles,
    totalSegments: dto.totalSegments,
    totalChars: dto.totalChars,
    completedFiles: dto.completedFiles,
    failedFiles: dto.failedFiles,
    progress: dto.progress,
    phase: validatePhase(dto.phase),
    bundleUrl: dto.bundleUrl,
    bundleState: validateBundleState(dto.bundleState ?? 'none'),
    bundleExpiresAt: dto.bundleExpiresAt,
    bundleCountTotal: 0,
    bundleCountSucceeded: 0,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    submittedAt: dto.submittedAt,
    finishedAt: dto.finishedAt
  }
}

